'use client';

import type { PodDetailOption } from '@slims/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/**
 * Il registro dei punti di connessione della configurazione
 * (`GET /api/energy/pods`).
 *
 * Serve dove il dettaglio di un POD si apre senza passare dalla classifica — la
 * riga di un'anomalia, un misuratore della scheda socio — e chi guarda vuole
 * comunque poter passare al POD accanto sullo stesso intervallo. Dalla
 * classifica la lista arriva già dalla scomposizione, e questo hook non serve.
 */
export interface PodRegistryEntry {
	id: string;
	codice: string;
	memberId: string;
	memberName: string | null;
	dso: string | null;
	cabinaPrimaria: string | null;
}

export interface UsePodsParams {
	/**
	 * Configurazione da leggere. Assente = lo scope amministrativo corrente; va
	 * passato dove la pagina ne mostra più di una affiancate.
	 */
	orgId?: string | null;
	enabled?: boolean;
}

const EMPTY: PodRegistryEntry[] = [];

/** React Query key for the POD registry of one org. */
export const podsKey = (orgId: string | null | undefined) => ['pods', orgId] as const;

export function usePods({ orgId: orgIdOverride, enabled = true }: UsePodsParams = {}) {
	const scopeOrgId = useAdminScopeStore((s) => s.configId);
	const orgId = orgIdOverride ?? scopeOrgId;
	const active = enabled && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: podsKey(orgId),
		enabled: active,
		queryFn: () => api.get<PodRegistryEntry[]>(`/api/energy/pods?orgId=${orgId}`),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	return {
		data: data ?? EMPTY,
		isLoading: active ? isLoading : false,
		error: error ? (error instanceof Error ? error.message : 'Errore nel caricamento dei POD') : null,
		refetch,
	};
}

/** Le voci del selettore POD, nella forma che il dettaglio legge. */
export function podOptions(pods: PodRegistryEntry[]): PodDetailOption[] {
	return pods.map((p) => ({ podId: p.id, codice: p.codice, memberName: p.memberName }));
}
